import React, {FC, memo, useMemo} from "react";
import {isBrowser} from "@montana/helpers/browser.helper";
import {IColor, IM25ColorCardCollection} from "@montana/models/generated/cms.interface";
import Carousel from "@montana/shared/carousel";
import {ColorSwatch} from "@montana/shared/color-swatch/color-swatch.component";
import {Link} from "@montana/shared/link";
import {MobileAndTabletPortrait, TabletAndLarger} from "@montana/shared/responsive";
import {RichText} from "@montana/shared/rich-text";
import {useSSRSafeId} from "@react-aria/ssr";
import cx from "clsx";
import {useMediaQuery} from "@montana/hooks/useMediaQuery";
import {useTranslations} from "@montana/containers/translations";

export const M25ColorCardCollection: FC<IM25ColorCardCollection> = memo((props: IM25ColorCardCollection) => {
    const {
        colors,
        galleryMode,
        header,
        link,
    } = props;

    const translate = useTranslations();

    const colorCards = useMemo(() => colors || [], [colors]);

    const isMobile = useMediaQuery('(max-width: 767px)');

    if (!isBrowser || colorCards.length === 0) {
        return <></>;
    }

    const uniqueId = useSSRSafeId();

    const renderSwatchCard = (color: IColor, index: number): React.ReactElement => {
        const colorId = color.colorId?.trim();
        const isSpacer = colorId === "Spacer";
        const key = colorId ? `${colorId}-${index}-${uniqueId}` : `color-${index}-${uniqueId}`;

        if (isSpacer) {
            return (
                <div
                    key={`spacer-${key}-${uniqueId}`}
                    aria-hidden="true"
                    className="color-card-collection__spacer"
                />
            );
        }

        const swatch = (
            <ColorSwatch
                {...color}
                ariaLabel={color.colorName || colorId || undefined}
                hoverLabel={translate(t => t.general.colorSwatchHoverLabel)}
                variant="card"
                size={!galleryMode && isMobile ? 'small' : 'large'}
            />
        );

        if (!color.link?.url) {
            return <div key={key} className="color-card-collection__card">{swatch}</div>;
        }

        return (
            <div key={key} className="color-card-collection__card color-card-collection__card--linked">
                <Link
                    to={color.link.url}
                    title={color.link.title || color.colorName || colorId}
                >
                    {swatch}
                </Link>
            </div>
        );
    };

    const renderGrid = (columns: number) => (
        <div className={`color-card-collection__grid color-card-collection__grid--columns-${columns}`}>
            {colorCards.map(renderSwatchCard)}
        </div>
    );

    const navigation = (
        <div className="color-card-collection__header">
            {header && <RichText className="color-card-collection__headline" text={header}/>}
            {!galleryMode && <div className="color-card-collection__header-actions">
                {link?.url && <Link to={link.url} withStroke>
                    {link.name ? link.name : translate(t => t.modulesAndBlocks.m25ViewAllLinkText, 'View all colours')}
                </Link>}
            </div>}
        </div>
    );

    return (
        <section className={cx("color-card-collection", {"color-card-collection--gallery": galleryMode})}>
            {galleryMode ? (
                <>
                    {navigation}
                    <MobileAndTabletPortrait>
                        {renderGrid(2)}
                    </MobileAndTabletPortrait>
                    <TabletAndLarger>
                        {renderGrid(7)}
                    </TabletAndLarger>
                </>
            ) : (
                <Carousel
                    header={navigation}
                    lowHeaderSpacing

                >
                    {colorCards
                        .filter((color) => color.colorId?.trim() !== "Spacer")
                        .map(renderSwatchCard)}
                </Carousel>
            )}
        </section>
    );
});

M25ColorCardCollection.displayName = "M25ColorCardCollection";
